<template>
  <div v-if="!skipFormLoading" class="md:w-568 mx-auto">
    <h1 class="text-h2 text-center mb-16">Add details for sitters</h1>
    <p class="text-body text-center max-w-368 mx-auto mb-32 md:mb-16">You can always edit this later</p>

    <Popup v-model="showPopupError" popupHeader="Error generating your draft" @close="closePopup">
      <Button variant="primary" value="Try again" size="medium" class="w-full" @click.prevent="retryChatGPT" />
    </Popup>

    <form class="flex flex-col mx-auto md:shadow-md md:p-24 bg-grey-background-subdued md:bg-white">
      <div class="flex flex-col items-center">
        <Button
          size="large"
          class="rounded-3xl text-center w-full normal-case chat-gpt"
          variant="outlined"
          :loading="loading"
          @click.prevent="useChatGPT"
        >
          <AIIcon class="w-18 pr-4" /><span>Draft with AI</span>
        </Button>
        <p class="text-body mt-16 mb-32 md:mb-16 text-grey-text-subdued text-center text-small">
          OpenAI will use the information you have provided about this job to generate your job description. The generated output
          may contain errors. You agree to review it before publishing and understand you are responsible for the information you
          post.
          <a
            class="underline"
            href="https://support.sittercity.com/hc/en-us/articles/23699389621019-Write-your-job-description-with-OpenAI"
            target="_blank"
          >
            Learn more
          </a>
        </p>
      </div>
      <div class="mb-32">
        <TextArea
          id="job-headline"
          ref="headlineRef"
          v-model.trim="headline"
          type="text"
          labelName="Headline"
          :characterCounter="true"
          :minCharacters="minHeadlineCharacterLength"
          :maxCharacters="maxHeadlineCharacterLength"
          :rows="2"
          helperText="Be specific and include details about your job"
          :presenceRequired="true"
          @input="
            () => {
              aiGenerated = false;
            }
          "
        />
      </div>
      <div class="mb-32">
        <TextArea
          id="job-simple-description"
          ref="descriptionRef"
          v-model.trim="description"
          labelName="Description"
          :minCharacters="minDescriptionCharacterLength"
          :maxCharacters="maxDescriptionCharacterLength"
          :rows="10"
          :presenceRequired="true"
          @input="
            () => {
              aiGenerated = false;
            }
          "
        />
      </div>
      <div class="flex flex-row-reverse">
        <RouteStepperNextButton
          ref="routeStepperNextButton"
          :stepData="descriptionReviewSection"
          :draftData="draftData"
          :validate="validate"
          @errors="errors = $event"
        />
      </div>
    </form>
  </div>
  <div v-else class="flex items-center justify-center">
    <Spinner class="my-112" :width="48" :height="48" />
  </div>
</template>

<script lang="ts">
// Components
import { PropType } from 'vue';
import Button from '@/components/button/Button.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import TextArea from '@/components/textarea/TextArea.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import Popup from '@/components/popup/Popup.vue';

// Icons
import AIIcon from '@/graphics/icons/AIIcon.vue';

// Helpers, GQL
import { jobDescriptionPrompt, jobHeadlinePrompt } from '@/helpers/chatGPTHelper';
import { OPENAI_MUTATION } from '@/graphql/text_generation/mutations';
import { JobPostDraft } from './types';
import RouteStepperNextButton from '../../components/route_stepper/RouteStepperNextButton.vue';

interface Errors {
  headline?: string;
  description?: string;
}

interface JobPostDetailsData {
  aiGenerated: boolean;
  headline: string;
  description: string;
  showPopupError: boolean;
  loading: boolean;
  skipFormLoading: boolean;
  errors: Errors;
}

export default {
  name: 'JobPostDetails',
  components: {
    // Components
    Button,
    Spinner,
    TextArea,
    Popup,

    // Icons
    AIIcon,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data(): JobPostDetailsData {
    return {
      aiGenerated: false,
      headline: '',
      description: '',
      showPopupError: false,
      loading: false,
      skipFormLoading: true,
      errors: {},
    };
  },
  computed: {
    minHeadlineCharacterLength(): number {
      return 10;
    },
    maxHeadlineCharacterLength(): number {
      return 80;
    },
    minDescriptionCharacterLength(): number {
      return 1;
    },
    maxDescriptionCharacterLength(): number {
      return 700;
    },
    canSubmit(): boolean {
      return (
        this.headline.length >= this.minHeadlineCharacterLength &&
        this.headline.length <= this.maxHeadlineCharacterLength &&
        this.description.length >= this.minDescriptionCharacterLength &&
        this.description.length <= this.maxDescriptionCharacterLength
      );
    },
    headlineReviewSection(): ReviewSection {
      // headlineReviewSection needs to be added to draft data instead of stepData
      // because design wants to display it out of order from the other review sections
      return {
        title: 'Headline',
        content: this.headline,
        editPath: this.$route.path,
      };
    },
    descriptionReviewSection(): ReviewSection {
      return {
        title: 'Description',
        content: this.description,
        editPath: this.$route.path,
      };
    },
    draftData(): JobPostDraft {
      return {
        headlineReviewSection: this.headlineReviewSection,
        title: this.headline,
        description: this.description,
        aiGenerated: this.aiGenerated,
      };
    },
  },
  created() {
    this.useChatGPT()
      .then(() => {
        this.$refs.routeStepperNextButton?.$el?.click();
      })
      .catch((error) => {
        console.error('Error during the process:', error);
      });
  },
  methods: {
    validate(): Errors {
      const errors: Errors = {};

      if (this.headline.length < this.minHeadlineCharacterLength) {
        errors.headline = `This field is required and must be at least ${this.minHeadlineCharacterLength} characters`;
      } else if (this.headline.length > this.maxHeadlineCharacterLength) {
        errors.headline = `This field is required and must be less than ${this.maxHeadlineCharacterLength} characters`;
      }

      if (this.description.length < this.minDescriptionCharacterLength) {
        errors.description = `Description must be at least ${this.minDescriptionCharacterLength} characters`;
      } else if (this.description.length > this.maxDescriptionCharacterLength) {
        errors.description = `Description must be less than ${this.maxDescriptionCharacterLength} characters`;
      }

      return errors;
    },
    async useChatGPT() {
      this.loading = true;

      const state = {
        requirements: this.draft.requirements,
        languages: this.draft.languages,
        numberOfChildren: this.draft.numberOfChildren,
        ageGroups: this.draft.ageGroups,
        schedule: this.draft.schedule,
        scheduleType: this.draft.scheduleType,
      };

      try {
        const response = await this.$apollo.mutate({
          mutation: OPENAI_MUTATION,
          variables: {
            prompt: jobHeadlinePrompt(state),
            maxTokens: 20, // 80 characters (max length) is 20 tokens https://help.openai.com/en/articles/4936856-what-are-tokens-and-how-to-count-them
          },
        });

        this.headline = response.data.chatGpt.response.trim().replace(/["]+/g, '');
      } catch (error) {
        this.showPopupError = true;
        console.error('Chatgpt error:', error);
      }

      try {
        const response = await this.$apollo.mutate({
          mutation: OPENAI_MUTATION,
          variables: {
            prompt: jobDescriptionPrompt(state),
            maxTokens: 125, // 500 characters (max length) is 125 tokens https://help.openai.com/en/articles/4936856-what-are-tokens-and-how-to-count-them
          },
        });

        this.description = response.data.chatGpt.response.trim();
        this.loading = false;
        this.skipFormLoading = false;
        await this.$nextTick();
        this.$refs.headlineRef?.checkCharCount();
        this.$refs.descriptionRef?.checkCharCount();
        this.aiGenerated = true;
      } catch (error) {
        this.showPopupError = true;
        this.loading = false;
        this.skipFormLoading = false;
      }
    },
    closePopup() {
      this.showPopupError = false;
    },
    retryChatGPT() {
      this.showPopupError = false;
      this.useChatGPT();
    },
  },
};
</script>
